import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RefreshCw, AlertTriangle, CheckCircle, Users, Database, ShieldAlert } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface UserNeedingMigration {
  userId: string;
  userEmail: string;
  invitationId: string;
  currentTenantName: string;
  inviterEmail: string;
}

interface MigrationResult {
  success: boolean;
  userId?: string;
  userEmail?: string;
  newTenantId?: string;
  newTenantName?: string;
  error?: string;
  dataMovedCounts?: {
    goals: number;
    habits: number;
    tasks: number;
    journalEntries: number;
    notes: number;
    activities: number;
  };
}

export default function AdminMigration() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [showDryRunDialog, setShowDryRunDialog] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationResults, setMigrationResults] = useState<MigrationResult[] | null>(null);

  // Check if user is admin
  const isAdmin = user?.role === "admin";

  // Fetch users needing migration
  const { data: usersNeedingMigration, isLoading: loadingUsers, error: loadingError, refetch } = useQuery({
    queryKey: ["/api/admin/migration/users-needing-migration"],
    select: (data) => data as UserNeedingMigration[],
    enabled: isAdmin, // Only fetch if user is admin
  });

  // Dry run mutation
  const dryRunMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/migration/migrate-all-users", { dryRun: true }),
    onSuccess: (data: any) => {
      setMigrationResults(data.results || []);
    },
    onError: (error: any) => {
    },
  });

  // Actual migration mutation
  const migrationMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/migration/migrate-all-users", { dryRun: false }),
    onSuccess: (data: any) => {
      setMigrationResults(data.results || []);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/migration/users-needing-migration"] });
      refetch();
    },
    onError: (error: any) => {
    },
  });

  const handleDryRun = () => {
    setShowDryRunDialog(false);
    setMigrationResults(null);
    dryRunMutation.mutate();
  };

  const handleMigration = () => {
    setShowMigrationDialog(false);
    setMigrationResults(null);
    migrationMutation.mutate();
  };

  const isPending = dryRunMutation.isPending || migrationMutation.isPending;

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6">
        <div className="text-center py-12">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show unauthorized if not admin
  if (!isAdmin) {
    return (
      <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-8 w-8 text-destructive" />
              <div>
                <CardTitle>Unauthorized Access</CardTitle>
                <CardDescription>
                  You do not have permission to access this page
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              This page is only accessible to administrators. If you believe you should have
              access, please contact your system administrator.
            </p>
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full py-6 md:py-8 px-4 md:px-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Tenant Migration</h1>
        <p className="text-muted-foreground">
          Migrate invited users to their own isolated tenants for complete data separation
        </p>
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-2 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Users Needing Migration</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="migration-count">
              {loadingUsers ? "..." : usersNeedingMigration?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Users currently in shared tenants
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Migration Status</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {usersNeedingMigration?.length === 0 ? (
                <span className="text-green-600 dark:text-green-400">Complete</span>
              ) : (
                <span className="text-amber-600 dark:text-amber-400">Pending</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {usersNeedingMigration?.length === 0
                ? "All users have isolated tenants"
                : "Action required for full isolation"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Information Alert */}
      <Alert className="mb-6">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>About Tenant Migration</AlertTitle>
        <AlertDescription>
          This process moves invited users from shared tenants to their own isolated tenants.
          Each user will get their own workspace with all their data (goals, tasks, habits, journal entries, etc.).
          <strong className="block mt-2">Always run a dry run first to preview changes safely.</strong>
        </AlertDescription>
      </Alert>

      {/* Users List */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Users Requiring Migration</CardTitle>
              <CardDescription>
                These users are currently in shared tenants and need their own isolated workspace
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={loadingUsers}
              data-testid="button-refresh"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingUsers ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingUsers ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : loadingError ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to Load Users</h3>
              <p className="text-muted-foreground mb-4">
                {(loadingError as any)?.message || "An error occurred while fetching users"}
              </p>
              <Button onClick={() => refetch()} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : usersNeedingMigration?.length === 0 ? (
            <div className="text-center py-8" data-testid="no-migrations">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">All Set!</h3>
              <p className="text-muted-foreground">
                No users need migration. All users have their own isolated tenants.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {usersNeedingMigration?.map((user) => (
                <div
                  key={user.userId}
                  className="flex items-center justify-between p-4 border rounded-lg"
                  data-testid={`migration-user-${user.userId}`}
                >
                  <div>
                    <div className="font-medium">{user.userEmail}</div>
                    <div className="text-sm text-muted-foreground">
                      Currently in: {user.currentTenantName} (invited by {user.inviterEmail})
                    </div>
                  </div>
                  <div className="text-sm text-amber-600 dark:text-amber-400">
                    Needs Migration
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {usersNeedingMigration && usersNeedingMigration.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Migration Actions</CardTitle>
            <CardDescription>
              Run a safe preview first, then execute the actual migration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setShowDryRunDialog(true)}
                disabled={isPending}
                className="flex-1"
                data-testid="button-dry-run"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                {dryRunMutation.isPending ? "Running Preview..." : "Run Dry Run (Safe Preview)"}
              </Button>
              <Button
                variant="default"
                onClick={() => setShowMigrationDialog(true)}
                disabled={isPending}
                className="flex-1"
                data-testid="button-migrate"
              >
                <Database className="h-4 w-4 mr-2" />
                {migrationMutation.isPending ? "Migrating..." : "Execute Migration"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Display */}
      {migrationResults && migrationResults.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>
              {dryRunMutation.isPending || migrationMutation.isPending
                ? "Processing..."
                : migrationMutation.isSuccess
                ? "Migration Results"
                : "Dry Run Preview"}
            </CardTitle>
            <CardDescription>
              {migrationMutation.isSuccess
                ? "Users have been successfully migrated"
                : "Preview of what will happen (no changes made yet)"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {migrationResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-4 border rounded-lg ${
                    result.success
                      ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950"
                      : "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"
                  }`}
                  data-testid={`result-${index}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium">{result.userEmail}</div>
                    {result.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  {result.success ? (
                    <div className="text-sm space-y-1">
                      <div className="text-muted-foreground">
                        New Tenant: {result.newTenantName}
                      </div>
                      {result.dataMovedCounts && (
                        <div className="text-xs text-muted-foreground mt-2">
                          Data moved: {result.dataMovedCounts.goals} goals,{" "}
                          {result.dataMovedCounts.habits} habits,{" "}
                          {result.dataMovedCounts.tasks} tasks,{" "}
                          {result.dataMovedCounts.journalEntries} journal entries,{" "}
                          {result.dataMovedCounts.notes} notes,{" "}
                          {result.dataMovedCounts.activities} activities
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-red-600 dark:text-red-400">
                      Error: {result.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dry Run Confirmation Dialog */}
      <AlertDialog open={showDryRunDialog} onOpenChange={setShowDryRunDialog}>
        <AlertDialogContent data-testid="dialog-dry-run" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Run Dry Run Preview?</AlertDialogTitle>
            <AlertDialogDescription>
              This will simulate the migration process without making any actual changes to your database.
              You'll see exactly what will happen when you run the real migration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-dry-run">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDryRun} data-testid="button-confirm-dry-run">
              Run Preview
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Migration Confirmation Dialog */}
      <AlertDialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
        <AlertDialogContent data-testid="dialog-migrate" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Execute Migration?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will migrate <strong>{usersNeedingMigration?.length || 0} user(s)</strong> to
                their own isolated tenants.
              </p>
              <p className="text-amber-600 dark:text-amber-400 font-semibold">
                ⚠️ This operation will make permanent changes to your database.
              </p>
              <p>
                Each user will get their own tenant with all their data moved over. This process is
                atomic and safe, but cannot be easily reversed.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-migrate">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMigration}
              className="bg-red-600 hover:bg-red-700"
              data-testid="button-confirm-migrate"
            >
              Yes, Execute Migration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
